import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CheckCircleFilled, ClockCircleFilled, CloseCircleFilled, CodeOutlined, TagsOutlined } from '@ant-design/icons';
import { CommitItem, FeatureItem, FixRequestStatusType, IssueEntity, PRItem, ReleaseStepType } from '@/types';
import { RootState } from '@/reducers/reducers';
import { antGrey, getAvatarColorForName, runwayColors, rwColors } from '@/components/styles/globalStyles';
import { selectFeatureManagementIntegration, selectUserById, selectVCSIntegration } from '@/selectors/selectors';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { routeForReleaseStepOrNav } from '@/helpers/routeHelpers';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { getFixRequestFormFieldsSummary } from '@/helpers';

const dotStyle = (color: string): React.CSSProperties => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
});

const isPRItem = (code: CommitItem | PRItem): code is PRItem => 'title' in code;
const codeIdentifierLabel = (code: CommitItem | PRItem): string =>
    isPRItem(code) ? `#${code.identifier}` : code.identifier.slice(0, 7);
const codeText = (code: CommitItem | PRItem): string => (isPRItem(code) ? code.title : code.message);

const statusMeta = (status: FixRequestStatusType): { label: string; icon: React.ReactNode } => {
    switch (status) {
        case FixRequestStatusType.FixRequestApproved:
            return { label: 'Approved', icon: <CheckCircleFilled style={{ color: rwColors.statusGreen }} /> };
        case FixRequestStatusType.FixRequestRejected:
            return { label: 'Rejected', icon: <CloseCircleFilled style={{ color: rwColors.statusRed }} /> };
        default:
            return {
                label: 'Pending approval',
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow }} />,
            };
    }
};

const OwnerAvatar: React.FC<{ name?: string; src?: string }> = ({ name, src }) => {
    if (!name) {
        return null;
    }
    return (
        <Avatar
            size={18}
            src={src}
            style={{ backgroundColor: getAvatarColorForName(name), fontSize: 10, flexShrink: 0, border: 'none' }}
        >
            {name.charAt(0).toUpperCase()}
        </Avatar>
    );
};

const itemLink = (label: string, url?: string) =>
    url ?
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: rwColors.primary, fontWeight: 500, flexShrink: 0 }}
        >
            {label}
        </a>
    :   <Text style={{ color: antGrey.grey8, fontWeight: 500, flexShrink: 0 }}>{label}</Text>;

const WorkRow: React.FC<{
    item: FeatureItem;
    ticketLogo?: string;
    codeLogo?: string;
    requesterName?: string;
    requesterAvatarSrc?: string;
    isLast: boolean;
    onGoToFeatureReadiness?: () => void;
}> = ({ item, ticketLogo, codeLogo, requesterName, requesterAvatarSrc, isLast, onGoToFeatureReadiness }) => {
    const { ticket, code, fixRequest } = item;
    const status = fixRequest?.status?.status;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderBottom: isLast ? undefined : `1px solid ${antGrey.grey5}`,
            }}
        >
            {ticket && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <span style={dotStyle(antGrey.grey7)} />
                    {ticketLogo && <img src={ticketLogo} width={16} height={16} alt="" />}
                    {itemLink(ticket.identifier, ticket.url)}
                    <OwnerAvatar name={requesterName} src={requesterAvatarSrc} />
                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                        {getFixRequestFormFieldsSummary(fixRequest?.formFieldsValues)}
                    </Text>
                </div>
            )}
            {ticket && code && <div style={{ width: 1, height: 20, backgroundColor: antGrey.grey4, flexShrink: 0 }} />}
            {code && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <span style={dotStyle(rwColors.statusYellow)} />
                    {codeLogo && <img src={codeLogo} width={16} height={16} alt="" />}
                    {itemLink(codeIdentifierLabel(code), code.url)}
                    <OwnerAvatar name={code.author} />
                    <Text ellipsis style={{ color: antGrey.grey8, fontSize: 13 }}>
                        {codeText(code)}
                    </Text>
                </div>
            )}
            {status && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    {statusMeta(status).icon}
                    <Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {statusMeta(status).label}
                    </Text>
                </div>
            )}
            {onGoToFeatureReadiness && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <div style={{ width: 1, height: 20, backgroundColor: antGrey.grey5 }} />
                    <Tooltip title="View work item in Feature Readiness" placement="top">
                        <TagsOutlined
                            onClick={onGoToFeatureReadiness}
                            style={{ color: runwayColors.primary, cursor: 'pointer', fontSize: 16, flexShrink: 0 }}
                        />
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

export const WorkSection: React.FC<{ issue: IssueEntity; orgId: string }> = ({ issue, orgId }) => {
    const history = useHistory();
    const workItems = issue.fixRequest?.workItems ?? [];
    const version = issue.fixRequest?.fixVersion;
    const fmIntegration = useSelector((state: RootState) => selectFeatureManagementIntegration(state, issue.appId));
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, issue.appId));
    const [ticketLogo] = fmIntegration?.id ? iconAndAltForIntegrationType(fmIntegration.id) : [undefined];
    const [codeLogo] = vcsIntegration?.id ? iconAndAltForIntegrationType(vcsIntegration.id) : [undefined];

    const requesterId = issue.fixRequest?.createdBy;
    const requester = useSelector((state: RootState) =>
        requesterId ? selectUserById(state, orgId, issue.appId, requesterId) : undefined,
    );
    const requesterName = requester ? displayUserNameLong(requester) : requesterId;

    if (workItems.length === 0) {
        return null;
    }

    const goToFeatureReadiness =
        version ?
            (item: FeatureItem) => {
                const base = routeForReleaseStepOrNav(orgId, issue.appId, version, ReleaseStepType.FeatureReadiness);
                const params = new URLSearchParams({ selectedItem: item.id });
                if (item.fixRequest?.id) {
                    params.set('selectedFix', item.fixRequest.id);
                }
                history.push(`${base}?${params.toString()}`);
            }
        :   undefined;

    return (
        <Collapse
            className="triage-collapse"
            ghost
            bordered={false}
            defaultActiveKey={['section']}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <CodeOutlined style={{ color: antGrey.grey7 }} />
                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Work</Text>
                        </div>
                    ),
                    children: (
                        <div style={{ padding: 16 }}>
                            <div style={{ border: `1px solid ${antGrey.grey5}`, borderRadius: 8, overflow: 'hidden' }}>
                                {workItems.map((wi, idx) => (
                                    <WorkRow
                                        key={wi.id}
                                        item={wi}
                                        ticketLogo={ticketLogo ?? undefined}
                                        codeLogo={codeLogo ?? undefined}
                                        requesterName={requesterName}
                                        requesterAvatarSrc={requester?.commsAvatars?.image32}
                                        isLast={idx === workItems.length - 1}
                                        onGoToFeatureReadiness={
                                            goToFeatureReadiness ? () => goToFeatureReadiness(wi) : undefined
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    );
};
